import { cn } from "@/lib/utils";
import { Header, ListBoxSection } from "react-aria-components";
import type { EmptyStateProps, ListContentProps } from "./Autocomplete.type";
import { AutocompleteListItem } from "./AutocompleteListItem";

export const EmptyState = <TValue,>({
  emptyMessage,
  noneOption,
  selectedKey,
}: EmptyStateProps<TValue>) => {
  // Create a read-only item for the empty message
  const emptyMessageItem = {
    id: "__empty_message__",
    label: emptyMessage,
    value: null,
    isNoneOption: false,
  };

  return (
    <>
      <AutocompleteListItem
        item={emptyMessageItem}
        selectedKey={null} // Never selected
        className="cursor-default pointer-events-none text-gray-500 text-center data-disabled:opacity-50"
      />
      {noneOption && (
        <AutocompleteListItem
          item={noneOption}
          selectedKey={selectedKey}
          className="border-t border-gray-100 mt-1"
        />
      )}
    </>
  );
};

export const ListContent = <TValue,>({
  displayStructure,
  selectedKey,
}: ListContentProps<TValue>) => (
  <>
    {/* Render ungrouped items */}
    {displayStructure.ungrouped.map((item) => (
      <AutocompleteListItem
        key={item.id}
        item={item}
        selectedKey={selectedKey}
      />
    ))}

    {/* Render grouped sections */}
    {Object.entries(displayStructure.grouped).map(
      ([groupName, groupItems], idx) => (
        <ListBoxSection
          key={`group-${groupName}`}
          className={cn(idx !== 0 && "pt-2")}
        >
          <Header className="px-3 py-2 text-xs text-gray-500 tracking-wide">
            {groupName}
          </Header>
          {groupItems.map((item) => (
            <AutocompleteListItem
              key={item.id}
              item={item}
              selectedKey={selectedKey}
              className="pl-6"
            />
          ))}
        </ListBoxSection>
      ),
    )}
  </>
);
